import React, { useState, SyntheticEvent } from 'react';
import { Field, Formik, FormikProps } from 'formik';
import { Grid } from '@inplayer-org/inplayer-ui';
import { connect, MapStateToProps } from 'react-redux';

// utils
import JSONParse from 'utils/json-parse-safe';

// types
import RootState from 'ducks/RootState';
import { OvpMetadata } from 'pages/MerchantPages/Assets/assetTypes';

// components
import Input from 'components/Fields/Input';
import ErrorMessage from 'components/ErrorMessage';
import StyledContainer from 'components/StyledContainer';
import { FullHeightForm, ContentContainer, FullHeightContainer } from 'components/SharedStyledElements';
import { Item } from 'components/ThumbnailItemPicker';
import Restricted from 'restrictions/roles/components/Restricted';
import BrightcoveVideoMenu from './FormikFields/Brightcove/BrightcoveVideoMenu';
import FormAccordionHelper, { SectionType } from '../../components/AccordionHelper';

const { Container, Cell } = Grid;

interface FormValues {
  account_id: string;
  video_id: string;
  player_id: string;
}

const defaultValues: FormValues = {
  account_id: '',
  video_id: '',
  player_id: 'default',
};

interface StateProps {
  externalAccount: {
    keys: { brightcove?: any };
  };
}

interface OwnProps {
  rawContent: string;
  onSubmit: (contentString: string, externalAssetId: string, metadata?: OvpMetadata) => void;
  formId: string;
  isReadOnly: boolean;
  closePanel: () => void;
}

type Props = OwnProps & StateProps;

const BrightcoveContent = ({
  rawContent,
  onSubmit,
  externalAccount: { keys: { brightcove: { keys: { account_id = '' } = {} } = {} } = {} },
  formId,
  isReadOnly,
  closePanel,
}: Props) => {
  const initialValues: FormValues = {
    ...defaultValues,
    account_id,
    ...JSONParse(rawContent).value,
  };

  const [metadata, setMetadata] = useState<OvpMetadata | undefined>(undefined);

  // Handle if fields should be disabled
  const isContentEditingDisabled = account_id && initialValues.account_id !== account_id;

  const handleManualChange = (inputName: string, setFieldValue: (fieldName: string, value: any) => void) => ({
    target,
  }: SyntheticEvent<HTMLInputElement>) => {
    const { value } = target as HTMLInputElement;

    setMetadata(undefined);
    setFieldValue(inputName, value);
  };

  const onItemSelect = ({ label: title, thumbnail: image }: Item) => {
    setMetadata({ title, image });
  };

  const handleSubmit = (data: FormValues) => {
    const contentString = JSON.stringify(data);
    const { video_id: externalAssetId } = data;

    onSubmit(contentString, externalAssetId, metadata);
  };

  return (
    <FullHeightContainer columns={1} rowGap="2rem" height="auto">
      <Cell>
        <Formik onSubmit={handleSubmit} initialValues={initialValues}>
          {({ setFieldValue }: FormikProps<FormValues>) => (
            <FullHeightForm id={formId}>
              <FormAccordionHelper initialValues={initialValues} index={SectionType.Content} closePanel={closePanel} />
              <ContentContainer withPadding>
                <Container rowGap="2rem" columns={1}>
                  <Cell>
                    <Field
                      tag="text_input_account_id"
                      name="account_id"
                      label="Account ID"
                      component={Input}
                      disabled={!!initialValues.account_id || isContentEditingDisabled || isReadOnly}
                    />
                  </Cell>
                  <Cell>
                    <Field
                      tag="text_input_video_id"
                      name="video_id"
                      label="Video ID"
                      component={Input}
                      onChange={handleManualChange('video_id', setFieldValue)}
                      disabled={isContentEditingDisabled || isReadOnly}
                    />
                  </Cell>
                  <Cell>
                    <Field
                      tag="text_input_player_id"
                      name="player_id"
                      label="Player ID"
                      component={Input}
                      onChange={handleManualChange('player_id', setFieldValue)}
                      disabled={isContentEditingDisabled || isReadOnly}
                    />
                  </Cell>
                  {isContentEditingDisabled ? (
                    <StyledContainer margin="1rem 0 0 0" columns={1}>
                      <Cell>
                        <ErrorMessage>
                          You can not change content of this asset, because it is not part of the currently integrated
                          Brightcove account.
                        </ErrorMessage>
                      </Cell>
                    </StyledContainer>
                  ) : (
                    <Cell>
                      <Restricted isRestricted={isReadOnly}>
                        <Field
                          name="video_id"
                          accountId={initialValues.account_id}
                          onSelect={onItemSelect}
                          component={BrightcoveVideoMenu}
                        />
                      </Restricted>
                    </Cell>
                  )}
                </Container>
              </ContentContainer>
            </FullHeightForm>
          )}
        </Formik>
      </Cell>
    </FullHeightContainer>
  );
};

export const mapStateToProps: MapStateToProps<StateProps, OwnProps, RootState> = (state) => ({
  externalAccount: state.externalAccount,
});

export default connect(mapStateToProps, null)(BrightcoveContent);
